import { useEffect, useId, useRef, type RefObject } from "react";
import { AccountMenuContent } from "./AccountMenuContent";
import type { AccountMenuModel } from "./useAccountMenuModel";

type AccountMenuProps = {
  model: AccountMenuModel;
  anchorRef: RefObject<HTMLElement | null>;
  onClose(): void;
};

export function AccountMenu({ model, anchorRef, onClose }: AccountMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    const menu = menuRef.current;
    const anchor = anchorRef.current;
    if (!menu || !anchor) return;

    const focusables = menu.querySelectorAll<HTMLElement>(
      'button, a[href], [tabindex]:not([tabindex="-1"])',
    );
    focusables[0]?.focus();

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (!menu || !anchor) return;
      if (menu.contains(target) || anchor.contains(target)) return;
      onClose();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        anchor?.focus();
        return;
      }
      if (event.key !== "Tab" || !menu) return;
      const items = [...menu.querySelectorAll<HTMLElement>(
        'button, a[href], [tabindex]:not([tabindex="-1"])',
      )].filter((el) => !el.hasAttribute("disabled"));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={menuRef}
      id={menuId}
      className="account-menu account-menu--popover"
      role="menu"
      aria-label="Account menu"
    >
      <AccountMenuContent model={model} onClose={onClose} />
    </div>
  );
}
